import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import HeroVideo from "@/components/HeroVideo";
import { homeAudio } from "@/lib/homeAudio";
import { MapPin, Camera, Users, Sparkles } from "lucide-react";

/**
 * Extendable, data-driven onboarding-style home carousel.
 * - Horizontal scroll-snap; NO auto-advance across screens.
 * - Screen 1 is the existing HeroVideo (which internally auto-rotates media).
 * - Screen 2 is a compact "value" screen with a single "Entra in ToClean" primary CTA.
 * - To add a future screen just push into the SCREENS array (id, kind, render, order, enabled).
 */
export default function HomeSwipe() {
  const { t } = useI18n();
  const scrollerRef = useRef(null);
  const [idx, setIdx] = useState(0);

  const SCREENS = [
    { id: "hero", enabled: true, order: 1, render: () => <HeroVideo /> },
    { id: "values", enabled: true, order: 2, render: () => <ValuesScreen t={t} /> },
    // Future screens: push { id, order, enabled, render } here — no rebuild required.
  ]
    .filter((s) => s.enabled)
    .sort((a, b) => a.order - b.order);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const w = el.clientWidth || 1;
      const n = Math.round(el.scrollLeft / w);
      if (n !== idx) setIdx(n);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [idx]);

  return (
    <section className="relative w-full flex-1 h-[calc(100vh-64px)]" data-testid="home-swipe">
      <div
        ref={scrollerRef}
        className="flex w-full h-full overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar"
        style={{ scrollSnapStop: "always" }}
        data-testid="home-swipe-scroller"
      >
        {SCREENS.map((s) => (
          <div
            key={s.id}
            data-testid={`home-swipe-screen-${s.id}`}
            className="shrink-0 w-full h-full snap-center overflow-y-auto"
          >
            {s.render()}
          </div>
        ))}
      </div>

      {/* Small position indicators (discreet) */}
      {SCREENS.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5 pointer-events-none" data-testid="home-swipe-indicators">
          {SCREENS.map((s, i) => (
            <span
              key={s.id}
              data-testid={`home-swipe-dot-${i}`}
              className="rounded-full transition-all"
              style={{
                width: i === idx ? 22 : 6,
                height: 6,
                background: i === idx ? "#10B981" : "rgba(255,255,255,0.55)",
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function ValuesScreen({ t }) {
  const nav = useNavigate();
  const items = [
    { icon: Camera, titleKey: "swipe_value_1_title", descKey: "swipe_value_1_desc" },
    { icon: MapPin, titleKey: "swipe_value_2_title", descKey: "swipe_value_2_desc" },
    { icon: Users, titleKey: "swipe_value_3_title", descKey: "swipe_value_3_desc" },
  ];
  // Which card is currently expanded (null = none). Tapping the same card
  // again collapses it. Tapping another card swaps the expansion so at most
  // one card is expanded at a time (guarantees no visual overlap on mobile).
  const [expanded, setExpanded] = React.useState(null);
  // Bug fix: this CTA used to be a plain <Link to="/map"> which navigated
  // WITHOUT stopping the ambient audio, so the track kept playing on the
  // map. It must match the HeroVideo CTA behaviour: fade-out the SINGLE
  // homeAudio singleton (no restart, no second instance), then navigate.
  const enterApp = React.useCallback((e) => {
    e.preventDefault();
    homeAudio.fadeOutAndStop(420).then(() => nav("/map"));
  }, [nav]);
  return (
    <div className="relative w-full min-h-full bg-gradient-to-br from-emerald-900 via-emerald-800 to-slate-900 text-white overflow-hidden" data-testid="home-swipe-values">
      <div className="absolute inset-0 grain-overlay opacity-40" aria-hidden />
      {/* Compact vertical rhythm — everything (kicker + title + 3 cards + CTA)
          must fit within an iPhone SE viewport (375×667, ~603 px available
          after the app header). Layout keeps 3-column grid at ALL breakpoints
          so cards never stack and overflow the fold. */}
      <div className="relative min-h-full flex flex-col items-start justify-center px-4 sm:px-8 md:px-16 max-w-5xl mx-auto py-3 sm:py-6">
        <div className="inline-flex items-center gap-2 chip bg-white/15 text-white mb-2 sm:mb-4 text-[11px] sm:text-xs">
          <Sparkles size={11} /> {t("swipe_values_kicker")}
        </div>
        <h2 className="font-heading text-lg sm:text-2xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.15] max-w-3xl">
          {t("swipe_values_title")}
        </h2>
        {/* 3-column grid at every breakpoint. Cards are minimalist on mobile
            (icon + short title + 2-line desc) to guarantee they never get cut.
            Cards are now interactive: tap to expand the description in full,
            tap again (or another card) to collapse. `items-start` on the
            grid keeps siblings' vertical size stable when one expands. */}
        <div className="mt-3 sm:mt-5 grid grid-cols-3 gap-2 sm:gap-4 w-full max-w-3xl items-start">
          {items.map((v, i) => {
            const isOpen = expanded === i;
            return (
              <button
                key={i}
                type="button"
                data-testid={`home-swipe-value-${i}`}
                data-expanded={isOpen ? "true" : "false"}
                aria-expanded={isOpen}
                onClick={() => setExpanded((prev) => (prev === i ? null : i))}
                className={`text-left w-full rounded-xl sm:rounded-2xl bg-white/10 hover:bg-white/15 backdrop-blur-md p-2.5 sm:p-4 border transition-colors ${isOpen ? "border-emerald-300/60 ring-1 ring-emerald-300/40" : "border-white/15"}`}
              >
                <div className="w-8 h-8 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl bg-emerald-500/25 text-emerald-100 flex items-center justify-center">
                  <v.icon size={15} />
                </div>
                <div className="mt-2 font-heading font-semibold text-white text-[12px] sm:text-base leading-tight sm:leading-snug">{t(v.titleKey)}</div>
                <div
                  data-testid={`home-swipe-value-${i}-desc`}
                  className={`mt-1 text-[11px] sm:text-sm text-white/80 leading-snug ${isOpen ? "" : "line-clamp-3 sm:line-clamp-none"}`}
                >
                  {t(v.descKey)}
                </div>
              </button>
            );
          })}
        </div>
        <div className="mt-4 sm:mt-7 w-full flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
          <Link to="/map" onClick={enterApp} data-testid="home-swipe-cta-enter" className="w-full sm:w-auto">
            <Button size="lg" className="w-full sm:w-auto rounded-full bg-white text-emerald-900 hover:bg-emerald-50 hover:text-emerald-950 h-11 sm:h-12 px-6 sm:px-8 text-sm sm:text-base font-semibold shadow-2xl">
              <MapPin size={15} className="mr-2" /> {t("swipe_cta_enter")}
            </Button>
          </Link>
          <div className="text-[11px] sm:text-sm text-white/70 max-w-xs">{t("swipe_cta_hint")}</div>
        </div>
      </div>
    </div>
  );
}
